import React from 'react';

import { Button, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { useAuth } from 'context/auth-context';
import { useLastCreditAlert } from 'context/last-credit-alert-context';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { UseMutationResult } from 'react-query';
import { AthleteKiloClass, ErrorMessage, KiloClassAthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

interface ModalCancelReservationProps {
  opened: boolean;
  athleteKiloClassId: string;
  cancelReservationMutation: UseMutationResult<
    AxiosResponse<null | AthleteKiloClass>,
    AxiosError<ErrorMessage>,
    string
  >;
  kiloClass: KiloClassAthleteRetrieve;
  handleClose: () => void;
}

const ModalCancelReservation = ({
  opened,
  athleteKiloClassId,
  cancelReservationMutation,
  kiloClass,
  handleClose,
}: ModalCancelReservationProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const { session } = useAuth();
  const { showRenewalNotification } = useLastCreditAlert();
  const athleteTracking = useAthleteTracking();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('athlete');
  const creditDeduction = kiloClass.reservationAndCancellationSettings.cancellationCreditDeduction || false;
  const lateFee = kiloClass.reservationAndCancellationSettings.cancellationFee || 0;

  const handleCancelReservation = (): void => {
    cancelReservationMutation.mutate(athleteKiloClassId, {
      onSuccess: response => {
        successNotification('Athlete reservation cancel success', `Reservation successfully cancelled.`);
        athleteTracking.trackClassCancelled(
          kiloClass.kiloClassId,
          kiloClass.kiloClassType.name,
          kiloClass?.consideredLateCancel,
          creditDeduction,
          lateFee,
        );

        if (response.data?.creditDispensation && response?.data.isLateCancel) {
          getAthletePackageAllLimitedCreditsUsed.mutate(
            { creditDispensation: response.data.creditDispensation },
            {
              onSuccess: res => {
                const alert = buildLastCreditAlert(res.data, session?.athlete?.firstName ?? '');
                if (alert) {
                  showRenewalNotification(alert);
                }
              },
              onError: error => {
                errorNotification(
                  'Error to check if all limited credits used',
                  error.response?.data.message,
                );
              },
            },
          );
        }

        handleClose();
      },
      onError: error => {
        errorNotification('Athlete reservation cancel failed', error.response?.data.message);
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Cancel Reservation?</Title>}
      size='md'
    >
      <Stack px='md'>
        {!kiloClass?.consideredLateCancel && (
          <Text>{`Are you sure you want to cancel this reservation?`}</Text>
        )}
        {kiloClass?.consideredLateCancel && lateFee === 0 && creditDeduction && (
          <Text>
            {`The cancellation window has closed. If you attempt to cancel your reservation now, a credit will
            be deducted from your package. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        {kiloClass?.consideredLateCancel && lateFee > 0 && creditDeduction && (
          <Text>
            {`The cancellation window has closed. If you attempt to cancel your reservation now, a credit will
            be deducted from your package and you will be charged a fee. Do you want to continue to cancel
            your reservation?`}
          </Text>
        )}
        {kiloClass?.consideredLateCancel && !creditDeduction && lateFee === 0 && (
          <Text>
            {`The cancellation window has closed. There is no fee or credit usage for cancelling this
            reservation. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        {kiloClass?.consideredLateCancel && !creditDeduction && lateFee > 0 && (
          <Text>
            {`The cancellation window has closed. You will be charged a fee for cancelling this reservation but
            there is no credit deduction. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        <Button
          mt='md'
          onClick={handleCancelReservation}
          color='red'
          loading={cancelReservationMutation.isLoading}
        >
          Cancel Reservation
        </Button>
        <Button mb='md' onClick={handleClose} variant='outline' color='gray'>
          Keep Reservation
        </Button>
      </Stack>
    </Modal>
  );
};

export default ModalCancelReservation;
